import React, { useState, useEffect } from 'react';
import { 
  TrendingUpIcon,
  CurrencyDollarIcon,
  ChatAlt2Icon,
  BellIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ShoppingBagIcon,
  StarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PlusIcon,
  SearchIcon,
  FilterIcon,
  RefreshIcon,
  CalendarIcon,
  LocationMarkerIcon,
  LightningBoltIcon,
  SparklesIcon,
  ChartBarIcon,
  CogIcon,
  LockClosedIcon
} from '@heroicons/react/outline';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SupplierNotificationCenter from './SupplierNotificationCenter';

const SupplierDashboard = () => {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Mock data - replace with real Firebase data
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalQuotes: 24,
      activeQuotes: 8,
      wonQuotes: 12,
      totalRevenue: 2500000,
      responseRate: 85,
      avgResponseTime: 2.5,
      thisMonthQuotes: 6,
      thisMonthRevenue: 450000,
      pendingQuotes: 4,
      urgentQuotes: 2,
      newMessages: 7,
      profileViews: 156
    },
    recentActivity: [
      {
        id: 1,
        type: 'quote_submitted',
        title: 'Quote submitted for Office Cleaning',
        buyer: 'TechCorp Nigeria',
        amount: 150000,
        timestamp: new Date(Date.now() - 30 * 60 * 1000),
        status: 'pending'
      },
      {
        id: 2,
        type: 'quote_won',
        title: 'Quote accepted for IT Equipment',
        buyer: 'StartupHub Lagos',
        amount: 200000,
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        status: 'won'
      },
      {
        id: 3,
        type: 'new_rfq',
        title: 'New RFQ: 500 Branded Paper Bags',
        buyer: 'PrintMaster Ltd',
        amount: 75000,
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
        status: 'new'
      }
    ],
    topOpportunities: [
      {
        id: 1,
        title: 'Rice Supply Contract',
        buyer: 'Restaurant Chain NG',
        budget: 500000,
        category: 'Food Supply',
        urgency: 'high',
        responses: 8,
        timeLeft: '2 days'
      },
      {
        id: 2,
        title: 'Office Equipment Supply',
        buyer: 'Corporate Solutions Ltd',
        budget: 300000,
        category: 'Office Equipment',
        urgency: 'medium',
        responses: 12,
        timeLeft: '5 days'
      },
      {
        id: 3,
        title: 'Cleaning Services',
        buyer: 'TechCorp Nigeria',
        budget: 150000,
        category: 'Cleaning',
        urgency: 'low',
        responses: 5,
        timeLeft: '1 week'
      }
    ],
    performanceMetrics: {
      responseTime: 2.5,
      winRate: 68,
      avgQuoteValue: 125000,
      customerSatisfaction: 4.8
    }
  });

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'won': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'new': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-orange-100 text-orange-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'quote_submitted': return <DocumentTextIcon className="h-5 w-5 text-blue-600" />;
      case 'quote_won': return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'new_rfq': return <BellIcon className="h-5 w-5 text-orange-600" />;
      default: return <DocumentTextIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading your supplier dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-2">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center mr-4">
                    <TrendingUpIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Supplier Dashboard
                    </h1>
                    <p className="text-gray-600 mb-2">
                      Welcome back, {userProfile?.fullName || user?.displayName || 'Supplier'}! 
                      Here's your business overview.
                    </p>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="flex items-center text-green-600">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        {dashboardData.stats.wonQuotes} quotes won this month
                      </span>
                      <span className="flex items-center text-orange-600">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {dashboardData.stats.pendingQuotes} pending responses
                      </span>
                      <span className="flex items-center text-blue-600">
                        <ChatAlt2Icon className="h-4 w-4 mr-1" />
                        {dashboardData.stats.newMessages} new messages
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowNotifications(true)}
                  className="relative px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
                >
                  <BellIcon className="h-5 w-5 mr-2 inline" />
                  Notifications
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    3
                  </span>
                </button>
                <button 
                  onClick={() => navigate('/market-feed')}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-300 font-semibold"
                >
                  Browse RFQs
                </button>
                <button 
                  onClick={signOut}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-300 font-semibold flex items-center"
                >
                  <LockClosedIcon className="h-4 w-4 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Quotes</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.stats.totalQuotes}</p>
                <div className="flex items-center mt-2">
                  <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+12% this month</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(dashboardData.stats.totalRevenue)}</p>
                <div className="flex items-center mt-2">
                  <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+8% this month</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Win Rate</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.stats.responseRate}%</p>
                <div className="flex items-center mt-2">
                  <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+5% this month</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <TrendingUpIcon className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Response Time</p>
                <p className="text-3xl font-bold text-gray-900">{dashboardData.stats.avgResponseTime}h</p>
                <div className="flex items-center mt-2">
                  <ArrowDownIcon className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">-0.5h this month</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <ClockIcon className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl border border-yellow-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-800">Pending Quotes</p>
                <p className="text-2xl font-bold text-yellow-900">{dashboardData.stats.pendingQuotes}</p>
                <p className="text-xs text-yellow-700">Need attention</p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl border border-red-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-800">Urgent Quotes</p>
                <p className="text-2xl font-bold text-red-900">{dashboardData.stats.urgentQuotes}</p>
                <p className="text-xs text-red-700">Time sensitive</p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <ExclamationIcon className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">New Messages</p>
                <p className="text-2xl font-bold text-blue-900">{dashboardData.stats.newMessages}</p>
                <p className="text-xs text-blue-700">Unread</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ChatAlt2Icon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200 p-4 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">Profile Views</p>
                <p className="text-2xl font-bold text-green-900">{dashboardData.stats.profileViews}</p>
                <p className="text-xs text-green-700">This month</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <EyeIcon className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', name: 'Overview', icon: EyeIcon },
                { id: 'opportunities', name: 'Top Opportunities', icon: LightningBoltIcon },
                { id: 'activity', name: 'Recent Activity', icon: ClockIcon },
                { id: 'insights', name: 'Smart Insights', icon: SparklesIcon },
                { id: 'performance', name: 'Performance', icon: ChartBarIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Motivational Banner */}
                <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-2xl p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-2">🚀 You're doing great!</h3>
                      <p className="text-orange-100 mb-3">
                        Your response rate is {dashboardData.stats.responseRate}% - that's above average! 
                        Keep up the excellent work and you'll see even more success.
                      </p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="flex items-center">
                          <TrendingUpIcon className="h-4 w-4 mr-1" />
                          +{dashboardData.stats.thisMonthQuotes} quotes this month
                        </span>
                        <span className="flex items-center">
                          <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                          {formatCurrency(dashboardData.stats.thisMonthRevenue)} revenue
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold">{dashboardData.stats.responseRate}%</div>
                      <div className="text-orange-100 text-sm">Win Rate</div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Quick Actions */}
                  <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <SparklesIcon className="h-5 w-5 mr-2 text-orange-600" />
                      Quick Actions
                    </h3>
                    <div className="space-y-3">
                      <button
                        onClick={() => navigate('/market-feed')}
                        className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                            <ShoppingBagIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Browse New RFQs</p>
                            <p className="text-sm text-gray-600">Find opportunities</p>
                          </div>
                        </div>
                        <ArrowUpIcon className="h-5 w-5 text-gray-400" />
                      </button>
                      
                      <button
                        onClick={() => navigate('/communication-hub')}
                        className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                            <ChatAlt2Icon className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Communication Hub</p>
                            <p className="text-sm text-gray-600">Manage conversations</p>
                          </div>
                        </div>
                        <ArrowUpIcon className="h-5 w-5 text-gray-400" />
                      </button>
                      
                      <button
                        onClick={() => navigate('/profile')}
                        className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                            <CogIcon className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Update Profile</p>
                            <p className="text-sm text-gray-600">Enhance visibility</p>
                          </div>
                        </div>
                        <ArrowUpIcon className="h-5 w-5 text-gray-400" />
                      </button>
                      
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-green-900">💡 Pro Tip</h4>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">New</span>
                        </div>
                        <p className="text-sm text-green-800 mb-3">
                          Suppliers who respond within 1 hour win 3x more quotes. Set up notifications to never miss an opportunity!
                        </p>
                        <button className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium">
                          Enable Notifications
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Performance Summary */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <ChartBarIcon className="h-5 w-5 mr-2 text-blue-600" />
                      Performance Summary
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Response Time</span>
                        <span className="font-semibold text-gray-900">{dashboardData.performanceMetrics.responseTime}h</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Win Rate</span>
                        <span className="font-semibold text-gray-900">{dashboardData.performanceMetrics.winRate}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '68%' }}></div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Avg Quote Value</span>
                        <span className="font-semibold text-gray-900">{formatCurrency(dashboardData.performanceMetrics.avgQuoteValue)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-orange-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Customer Satisfaction</span>
                        <div className="flex items-center">
                          <span className="font-semibold text-gray-900 mr-2">{dashboardData.performanceMetrics.customerSatisfaction}</span>
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <StarIcon 
                                key={star} 
                                className={`h-4 w-4 ${
                                  star <= Math.floor(dashboardData.performanceMetrics.customerSatisfaction)
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`} 
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Top Opportunities Tab */}
            {activeTab === 'opportunities' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Top Opportunities</h3>
                  <button className="flex items-center text-sm text-orange-600 hover:text-orange-700">
                    <RefreshIcon className="h-4 w-4 mr-1" />
                    Refresh
                  </button>
                </div>
                
                <div className="space-y-4">
                  {dashboardData.topOpportunities.map((opportunity) => (
                    <div key={opportunity.id} className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="font-semibold text-gray-900">{opportunity.title}</h4>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUrgencyColor(opportunity.urgency)}`}>
                              {opportunity.urgency.charAt(0).toUpperCase() + opportunity.urgency.slice(1)} Priority
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-6 text-sm text-gray-600 mb-3">
                            <span className="flex items-center">
                              <UserGroupIcon className="h-4 w-4 mr-1" />
                              {opportunity.buyer}
                            </span>
                            <span className="flex items-center">
                              <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                              {formatCurrency(opportunity.budget)}
                            </span>
                            <span className="flex items-center">
                              <DocumentTextIcon className="h-4 w-4 mr-1" />
                              {opportunity.category}
                            </span>
                            <span className="flex items-center">
                              <ChatAlt2Icon className="h-4 w-4 mr-1" />
                              {opportunity.responses} responses
                            </span>
                          </div>
                          
                          <div className="flex items-center text-sm text-gray-500">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            <span>{opportunity.timeLeft} left to respond</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm">
                            View Details
                          </button>
                          <button className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 text-sm">
                            Submit Quote
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Activity Tab */}
            {activeTab === 'activity' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                  <button className="text-sm text-orange-600 hover:text-orange-700">View All</button>
                </div>
                
                <div className="space-y-4">
                  {dashboardData.recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        {getActivityIcon(activity.type)}
                      </div>
                      
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{activity.title}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          <span>{activity.buyer}</span>
                          <span>•</span>
                          <span>{formatCurrency(activity.amount)}</span>
                          <span>•</span>
                          <span>{formatTime(activity.timestamp)}</span>
                        </div>
                      </div>
                      
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                        {activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Smart Insights Tab */}
            {activeTab === 'insights' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Smart Insights & Recommendations</h3>
                  <span className="text-sm text-gray-500">AI-powered suggestions</span>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Market Trends */}
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <TrendingUpIcon className="h-5 w-5 mr-2 text-purple-600" />
                      Market Trends
                    </h4>
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">Office Equipment</span>
                          <span className="text-sm text-green-600 font-semibold">+23%</span>
                        </div>
                        <p className="text-xs text-gray-600">High demand in Lagos area</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">Cleaning Services</span>
                          <span className="text-sm text-green-600 font-semibold">+15%</span>
                        </div>
                        <p className="text-xs text-gray-600">Growing corporate demand</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">Food Supply</span>
                          <span className="text-sm text-red-600 font-semibold">-8%</span>
                        </div>
                        <p className="text-xs text-gray-600">Seasonal decline expected</p>
                      </div>
                    </div>
                  </div>

                  {/* Recommendations */}
                  <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <SparklesIcon className="h-5 w-5 mr-2 text-orange-600" />
                      Smart Recommendations
                    </h4>
                    <div className="space-y-3">
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-start">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                            <span className="text-xs font-bold text-blue-600">1</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Optimize your profile</p>
                            <p className="text-xs text-gray-600">Add more photos and certifications to increase visibility by 40%</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-start">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1">
                            <span className="text-xs font-bold text-green-600">2</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Respond faster</p>
                            <p className="text-xs text-gray-600">Quotes submitted within 2 hours have 3x higher win rate</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-start">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-1">
                            <span className="text-xs font-bold text-purple-600">3</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Expand service area</p>
                            <p className="text-xs text-gray-600">Consider adding Abuja to reach 25% more potential clients</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Tips */}
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <LightningBoltIcon className="h-5 w-5 mr-2 text-blue-600" />
                    Pro Tips for Success
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <ClockIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">Quick Response</p>
                      <p className="text-xs text-gray-600">Reply within 2 hours</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <StarIcon className="h-6 w-6 text-green-600" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">Quality Photos</p>
                      <p className="text-xs text-gray-600">Show your best work</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <ChatAlt2Icon className="h-6 w-6 text-purple-600" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">Clear Communication</p>
                      <p className="text-xs text-gray-600">Be professional & detailed</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Performance Tab */}
            {activeTab === 'performance' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Performance Analytics</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">This Month</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Quotes Submitted</span>
                        <span className="font-semibold">{dashboardData.stats.thisMonthQuotes}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Revenue Generated</span>
                        <span className="font-semibold">{formatCurrency(dashboardData.stats.thisMonthRevenue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Response Rate</span>
                        <span className="font-semibold">{dashboardData.stats.responseRate}%</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Goals & Targets</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Monthly Quotes Target</span>
                        <span className="font-semibold">10/15</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '67%' }}></div>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Revenue Target</span>
                        <span className="font-semibold">₦450K/₦500K</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '90%' }}></div>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Win Rate Target</span>
                        <span className="font-semibold">85%/90%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-orange-500 h-2 rounded-full" style={{ width: '94%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification Center Modal */}
      <SupplierNotificationCenter 
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </div>
  );
};

export default SupplierDashboard; 